import React, { useState, useEffect } from "react";

// ** Icons
import OpenInNew from "mdi-material-ui/OpenInNew";
import ReceiptTextOutline from "mdi-material-ui/ReceiptTextOutline";
import AlertCircleOutline from "mdi-material-ui/AlertCircleOutline";

// ** Layout & data
import UserLayout from "src/layouts/UserLayout";
import BackToCourses from "src/@core/components/salespark/BackToCourses";
import { purchasesList } from "src/api/purchases";

// ** Hooks & styles
import { useAuth } from "src/hooks/useAuth";
import "src/styles/style-v3.css";

// Native formatters (EUR / pt-PT) — no extra deps
const moneyFmt = new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" });
const dateFmt = new Intl.DateTimeFormat("pt-PT", { day: "2-digit", month: "long", year: "numeric" });
const formatMoney = (v) => (typeof v === "number" ? moneyFmt.format(v) : "");
const formatDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? "" : dateFmt.format(dt);
};

/******************************************************************
 * ##: MyPurchases
 * Learner "As minhas compras": lists the student's paid purchases
 * (matched by their account e-mail) and deep-links each one to its
 * existing Checkout billing page (fatura). No billing logic lives
 * here — the API returns a ready billing_url per purchase.
 * @returns {JSX.Element} - the purchases page
 * History:
 * 07-10-2026: Created
 ******************************************************************/
const MyPurchases = () => {
  // ** Hooks
  const auth = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [purchases, setPurchases] = useState([]);
  const [errorLoadingData, setErrorLoadingData] = useState(false);

  useEffect(() => {
    if (auth?.user) loadPurchases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPurchases = async () => {
    try {
      const response = await purchasesList(auth.user);
      if (!response || !response.status) {
        setErrorLoadingData(true);
      } else {
        setPurchases(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      setErrorLoadingData(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <UserLayout>
      <div className="spv3">
        <div className="spv3-pagehead">
          <div className="spv3-pagehead__text">
            <span className="spv3-eyebrow">A minha conta</span>
            <h1 className="spv3-pagehead__title">As minhas compras</h1>
          </div>
          <BackToCourses />
        </div>

        <section className="spv3-section">
          {errorLoadingData ? (
            <ErrorState />
          ) : isLoading ? (
            <div className="spv3-purchases">
              {Array.apply(null, { length: 4 }).map((e, i) => (
                <PurchaseRowSkeleton key={i} />
              ))}
            </div>
          ) : purchases.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="spv3-purchases">
              {purchases.map((item, i) => (
                <PurchaseRow key={item.purchase_id || i} data={item} />
              ))}
            </div>
          )}
        </section>
      </div>
    </UserLayout>
  );
};

// HELPERS

/******************************************************************
 * ##: PurchaseRow
 * One purchase row: product, date, total, a "Reembolsado" badge when
 * refunded, and a button opening the Checkout billing page (new tab).
 * @param {Object} props - data (one purchase from /users/purchases)
 * @returns {JSX.Element} - the `.spv3-purchase` row
 * History:
 * 07-10-2026: Created
 ******************************************************************/
const PurchaseRow = ({ data }) => (
  <div className="spv3-purchase">
    <div className="spv3-purchase__body">
      <h3 className="spv3-purchase__title">{data.product}</h3>
      <div className="spv3-purchase__meta">
        {formatDate(data.date) && <span>{formatDate(data.date)}</span>}
        {typeof data.total === "number" && <span className="spv3-purchase__total">{formatMoney(data.total)}</span>}
        {data.refund && <span className="spv3-pill spv3-pill--refund">Reembolsado</span>}
      </div>
    </div>
    {data.billing_url && (
      <a
        className="spv3-btn spv3-btn--primary spv3-purchase__action"
        href={data.billing_url}
        target="_blank"
        rel="noopener noreferrer"
      >
        Ver fatura
        <OpenInNew />
      </a>
    )}
  </div>
);

/******************************************************************
 * ##: PurchaseRowSkeleton
 * Loading placeholder matching the purchase row layout.
 * @returns {JSX.Element} - a shimmering `.spv3-purchase` row
 * History:
 * 07-10-2026: Created
 ******************************************************************/
const PurchaseRowSkeleton = () => (
  <div className="spv3-purchase">
    <div className="spv3-purchase__body">
      <span className="spv3-skeleton spv3-sk-line" style={{ display: "block", width: "55%" }} />
      <span className="spv3-skeleton spv3-sk-line" style={{ display: "block", width: "30%", marginTop: 10 }} />
    </div>
    <span className="spv3-skeleton" style={{ width: 128, height: 46, borderRadius: 11, flexShrink: 0 }} />
  </div>
);

/******************************************************************
 * ##: EmptyState
 * Shown when the learner has no purchases on their account.
 * @returns {JSX.Element} - the `.spv3-empty` panel
 * History:
 * 07-10-2026: Created
 ******************************************************************/
const EmptyState = () => (
  <div className="spv3-empty">
    <span className="spv3-empty__icon">
      <ReceiptTextOutline />
    </span>
    <h3 className="spv3-empty__title">Ainda não tens compras</h3>
    <p className="spv3-empty__text">
      Quando comprares um produto ou curso, a respetiva fatura aparece aqui, pronta a consultar.
    </p>
  </div>
);

/******************************************************************
 * ##: ErrorState
 * Shown when the purchases list fails to load.
 * @returns {JSX.Element} - the `.spv3-error` panel
 * History:
 * 07-10-2026: Created
 ******************************************************************/
const ErrorState = () => (
  <div className="spv3-error">
    <span className="spv3-error__icon">
      <AlertCircleOutline />
    </span>
    <h3 className="spv3-error__title">Não foi possível carregar as compras</h3>
    <p className="spv3-error__text">
      Tenta novamente mais tarde. Se o erro continuar, contacta o suporte através de <strong>suporte@salespark.io</strong>.
    </p>
  </div>
);

export default MyPurchases;
